import axios from 'axios';

/**
 * Partner-facing KYC portal API client.
 *
 * Deliberately separate from utils/axiosConfig: portal users are NOT internal
 * users. Auth is an opaque token in the X-KYC-Portal-Token header (stored in
 * localStorage), no cookies, and a 401/expired session routes to the portal
 * login — never to the internal /login.
 */
const baseURL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_API_URL || 'http://localhost:8001').replace(/\/api\/?$/, '');

const TOKEN_KEY = 'kyc_portal_token';

const portalAxios = axios.create({ baseURL });

portalAxios.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers['X-KYC-Portal-Token'] = token;
    }
    return config;
});

portalAxios.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem(TOKEN_KEY);
            if (!window.location.pathname.startsWith('/kyc-portal/login')) {
                window.location.href = '/kyc-portal/login';
            }
        }
        return Promise.reject(error);
    }
);

export const kycPortalService = {
    hasSession: () => Boolean(localStorage.getItem(TOKEN_KEY)),

    clearSession: () => localStorage.removeItem(TOKEN_KEY),

    validateInvite: async (token) => {
        const response = await portalAxios.get(`/api/kyc/portal/invite/${token}/`);
        return response.data;
    },

    acceptInvite: async ({ token, fullName, password }) => {
        const response = await portalAxios.post('/api/kyc/portal/accept-invite/', {
            token,
            full_name: fullName,
            password,
        });
        localStorage.setItem(TOKEN_KEY, response.data.token);
        return response.data;
    },

    login: async (email, password) => {
        const response = await portalAxios.post('/api/kyc/portal/login/', { email, password });
        localStorage.setItem(TOKEN_KEY, response.data.token);
        return response.data;
    },

    logout: async () => {
        try {
            await portalAxios.post('/api/kyc/portal/logout/');
        } finally {
            localStorage.removeItem(TOKEN_KEY);
        }
    },

    getCase: async () => {
        const response = await portalAxios.get('/api/kyc/portal/case/');
        return response.data;
    },

    saveForm: async (formData) => {
        const response = await portalAxios.patch('/api/kyc/portal/form/', { form_data: formData });
        return response.data;
    },

    uploadDocument: async (docType, file) => {
        const data = new FormData();
        data.append('doc_type', docType);
        data.append('file', file);
        const response = await portalAxios.post('/api/kyc/portal/documents/', data, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return response.data;
    },

    deleteDocument: async (documentId) => {
        const response = await portalAxios.delete(`/api/kyc/portal/documents/${documentId}/`);
        return response.data;
    },

    runExtraction: async () => {
        const response = await portalAxios.post('/api/kyc/portal/run-extraction/');
        return response.data;
    },

    getDeclaration: async () => {
        const response = await portalAxios.get('/api/kyc/portal/declaration/');
        return response.data;
    },

    signDeclaration: async ({ signerName, signerTitle, consent, countryOfIncorporation }) => {
        const response = await portalAxios.post('/api/kyc/portal/declaration/sign/', {
            signer_name: signerName,
            signer_title: signerTitle,
            consent,
            country_of_incorporation: countryOfIncorporation,
        });
        return response.data;
    },

    downloadDeclaration: async () => {
        const response = await portalAxios.get('/api/kyc/portal/declaration/download/', {
            responseType: 'blob',
        });
        return response.data;
    },

    submit: async () => {
        const response = await portalAxios.post('/api/kyc/portal/submit/');
        return response.data;
    },
};

export default kycPortalService;
